import { useState, useEffect } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { useAuth } from '../contexts/AuthContext';

interface UserPreferences {
  language: string;
  theme: 'light' | 'dark' | 'system';
  timerSettings: {
    inspection: boolean;
    holdDuration: number;
  };
}

export interface Cube {
  id: string;
  brand: string;
  model: string;
  nickname?: string;
  mainCube: boolean;
  addedAt: Date;
  magneticLevel?: number; // 1-5 scale
  lubrication?: {
    lastLubed?: Date;
    lubricant?: string;
  };
}

interface Solve {
  id: string;
  time: number;
  timestamp: number;
  scramble: string;
  penalty?: '+2' | 'DNF';
  cubeId?: string; // Reference to the cube used for this solve
}

interface TrainingList {
  id: string;
  name: string;
  description?: string;
  scrambles: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface UserData {
  email: string;
  displayName: string;
  photoURL: string;
  createdAt: Date;
  lastLogin: Date;
  solves: Solve[];
  preferences: UserPreferences;
  cubes: Cube[];
  trainingLists: TrainingList[];
  isAdmin?: boolean;
}

export const useUserData = (userId?: string) => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const effectiveUserId = userId || currentUser?.uid;
        if (!effectiveUserId) {
          setLoading(false);
          return;
        }

        const userRef = doc(db, 'users', effectiveUserId);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          // Convert Firestore timestamps to JavaScript dates
          const processedData = {
            id: userDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            lastLogin: data.lastLogin?.toDate?.() || new Date(),
            // Process cubes array if it exists
            cubes: data.cubes?.map((cube: any) => ({
              ...cube,
              addedAt: cube.addedAt?.toDate?.() || new Date(),
              lubrication: cube.lubrication ? {
                ...cube.lubrication,
                lastLubed: cube.lubrication.lastLubed?.toDate?.() || null,
              } : undefined,
            })) || [],
            // Process training lists if they exist
            trainingLists: data.trainingLists?.map((list: any) => ({
              ...list,
              createdAt: list.createdAt?.toDate?.() || new Date(),
              updatedAt: list.updatedAt?.toDate?.() || new Date(),
            })) || [],
          };
          setUserData(processedData);
        } else {
          setUserData(null);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err as Error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser, userId]);

  return { userData, loading, error };
}; 